import type { ProductItem } from '@/lib/api/products';
import type { CurrencyOptionsType } from '@/components/form/currency-price-input';
import type { LockedBy } from '@/lib/currency/currencyOptionsKeys';

export interface BulkEditVariantSource {
  id: string;
  productId: string;
  default: boolean;
  name: string;
  description: string | null;
  sku: string | null;
  grams: number | null;
  imageUrl: string | null;
  active: boolean;
  inStock: boolean;
  creditPrice: number | null;
  prices: Array<{
    id: string;
    default: boolean;
    currencyOptions: Record<string, { amount: number; date?: string; rate?: number; lock?: boolean; lockedBy?: LockedBy }>;
    recurring: boolean;
    interval: string | null;
    intervalCount: number | null;
    rebillWithOrder?: boolean;
    rebillMode?: string | null;
    addDeliveryOnRebill?: boolean;
    isExternalSellingPlan?: boolean;
  }>;
}

export type BulkEditRowKind = 'product' | 'variant';

export type BulkEditStaticColumnId =
  | 'description'
  | 'unitLabel'
  | 'accountingCode'
  | 'mappedTaxCategory'
  | 'price'
  | 'creditPrice'
  | 'sku'
  | 'weight'
  | 'status'
  | 'stock'
  | 'default'
  | 'recurring'
  | 'interval'
  | 'intervalCount'
  | 'shippable'
  | 'taxable'
  | 'sync';

export type BulkEditColumnId = BulkEditStaticColumnId;

export interface BulkEditProductEdits {
  name?: string;
  productDescription?: string;
  unitLabel?: string | null;
  accountingCode?: string | null;
  taxCategory?: string | null;
  mappedTaxCategoryId?: string | null;
  active?: boolean;
  isShippable?: boolean;
  isTaxable?: boolean;
  autoSync?: boolean;
}

export interface BulkEditVariantEdits {
  name?: string;
  variantDescription?: string;
  sku?: string | null;
  grams?: number | null;
  active?: boolean;
  inStock?: boolean;
  /** Per-currency price edits (minor units). Keys are upper-case ISO codes. */
  currencyEdits?: Record<string, number>;
  creditPrice?: number | null;
  imageUrl?: string | null;
  isDefault?: boolean;
  recurring?: boolean;
  interval?: string | null;
  intervalCount?: number | null;
}

export type BulkEditRowEdits = BulkEditProductEdits & BulkEditVariantEdits;

export interface BulkEditMappedTaxCategory {
  id: string;
  slug: string;
  label: string;
  taxjarCode: string | null;
}

export interface BulkEditRow {
  key: string;
  kind: BulkEditRowKind;
  productId: string;
  variantId?: string;
  name: string;
  imageUrl: string | null;
  hasChildVariants: boolean;
  isExpanded: boolean;
  variantCount: number;
  isLastVariantInGroup?: boolean;
  active: boolean;
  isShippable: boolean;
  isTaxable: boolean;
  autoSync: boolean;
  productDescription: string;
  variantDescription: string;
  unitLabel: string;
  accountingCode: string;
  taxCategory: string;
  mappedTaxCategoryId: string | null;
  mappedTaxCategory: BulkEditMappedTaxCategory | null;
  sku: string;
  grams: number | null;
  inStock: boolean;
  priceAmount: number | null;
  creditPrice: number | null;
  priceCurrency: string;
  defaultPriceId: string | null;
  currencyOptions: CurrencyOptionsType;
  isDefault: boolean;
  recurring: boolean;
  interval: string | null;
  intervalCount: number | null;
  sourceProduct?: ProductItem;
  sourceVariant?: BulkEditVariantSource;
}

export const ALL_BULK_EDIT_STATIC_COLUMN_IDS: BulkEditStaticColumnId[] = [
  'description',
  'unitLabel',
  'accountingCode',
  'mappedTaxCategory',
  'price',
  'creditPrice',
  'sku',
  'weight',
  'status',
  'stock',
  'default',
  'recurring',
  'interval',
  'intervalCount',
  'shippable',
  'taxable',
  'sync',
];

export const DEFAULT_VISIBLE_STATIC_COLUMNS: BulkEditStaticColumnId[] = [
  'description',
  'mappedTaxCategory',
  'price',
  'sku',
  'weight',
  'status',
  'stock',
  'shippable',
  'taxable',
  'sync',
  'default',
  'recurring',
  'interval',
  'intervalCount',
];

/** Static columns only — price columns are added dynamically per store currencies. */
export const ALL_BULK_EDIT_COLUMN_IDS: BulkEditColumnId[] = [...ALL_BULK_EDIT_STATIC_COLUMN_IDS];

/** Static defaults — price columns merged in useBulkEditState when store loads. */
export const DEFAULT_VISIBLE_COLUMNS: BulkEditColumnId[] = [...DEFAULT_VISIBLE_STATIC_COLUMNS];
